/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSSRSettings } from './SSRSettingsContext';
import { setAdConsent, setAnalyticsConsent } from '../lib/gtag';

declare global {
  interface Window {
    __INITIAL_SETTINGS__?: Record<string, unknown>;
  }
}

// Bump CONSENT_VERSION (and the storage key) to force re-consent for every
// visitor. Mirrored in the inline gtag snippet (server/config.ts), which seeds
// the consent default from this exact localStorage record — keep the two in
// sync when either changes.
const CONSENT_VERSION = 3;
const STORAGE_KEY = 'cookie_consent_v3';
const LEGACY_KEYS = ['cookie-consent', 'cookie_consent_v2'];

export interface CookiePreferences {
  functional: boolean;
  analytical: boolean;
  marketing: boolean;
}

interface ConsentRecord {
  preferences: CookiePreferences;
  timestamp: string;
  version: number;
}

interface CookieContextType {
  hasConsent: boolean;
  preferences: CookiePreferences;
  cookiewallEnabled: boolean;
  acceptAll: () => void;
  acceptNecessary: () => void;
  savePreferences: (prefs: { analytical: boolean; marketing: boolean }) => void;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  functional: true,
  analytical: false,
  marketing: false,
};

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ssrSettings = useSSRSettings();
  // Seed cookiewallEnabled from the SSR / window-injected settings so we
  // don't render the page once without the cookiewall and then pop it in
  // a beat later when /api/settings resolves.
  const seedCookiewall = (): boolean => {
    if (ssrSettings && typeof ssrSettings.cookiewall_enabled === 'boolean') {
      return ssrSettings.cookiewall_enabled;
    }
    if (typeof window !== 'undefined' && window.__INITIAL_SETTINGS__) {
      const v = window.__INITIAL_SETTINGS__.cookiewall_enabled;
      if (typeof v === 'boolean') return v;
    }
    return false;
  };

  const [hasConsent, setHasConsent] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [cookiewallEnabled, setCookiewallEnabled] = useState<boolean>(seedCookiewall);

  useEffect(() => {
    for (const key of LEGACY_KEYS) {
      if (localStorage.getItem(key)) localStorage.removeItem(key);
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as ConsentRecord;
      if (parsed?.version !== CONSENT_VERSION || !parsed?.preferences) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      // A transition (2026-10-15): this is the first update above the route's
      // Suspense boundary after hydration, and the provider's new value reaches
      // that boundary. While the baked page's lazy chunk is still loading, a
      // plain update there makes React client-render the route (React #421:
      // the baked <main> empties until the chunk is in); a transition waits
      // for the chunk. Nothing visible depends on the timing — the banner
      // waits 1.5 s before it shows, the consent push to gtag can follow —
      // docs/reference-ssr-prerendering.md, "Hydration keeps the baked HTML".
      startTransition(() => {
        setPreferences({
          functional: true,
          analytical: !!parsed.preferences.analytical,
          marketing: !!parsed.preferences.marketing,
        });
        setHasConsent(true);
      });
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Skip the API fetch on first mount when the prerender already seeded
  // cookiewall state. Subsequent admin saves still flow through /api/settings
  // on the next page load.
  useEffect(() => {
    const seededFromWindow = typeof window !== 'undefined' && Boolean(window.__INITIAL_SETTINGS__);
    if (ssrSettings || seededFromWindow) return;
    fetch('/api/settings')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data?.cookiewall_enabled === 'boolean') {
          setCookiewallEnabled(data.cookiewall_enabled);
        }
      })
      .catch((err) => {
        // Surface failures during local dev — silently swallowing them in
        // production avoids noisy console output for transient blips, but in
        // dev we want to know if /api/settings is broken.
        if (import.meta.env.DEV) console.warn('[CookieContext] /api/settings fetch failed:', err);
      });
  }, [ssrSettings]);

  const persist = useCallback((prefs: CookiePreferences) => {
    // Push consent to gtag SYNCHRONOUSLY, before any state update: the
    // cookie_consent tracking event fires in the same tick as the decision,
    // and dataLayer processes pushes in order — waiting for the React effect
    // (next render) would dispatch that event under the old consent state.
    setAnalyticsConsent(prefs.analytical);
    setAdConsent(prefs.marketing);
    const record: ConsentRecord = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // localStorage may be unavailable (private mode); ignore
    }
    setPreferences(prefs);
    setHasConsent(true);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ functional: true, analytical: true, marketing: true });
  }, [persist]);

  const acceptNecessary = useCallback(() => {
    persist({ functional: true, analytical: false, marketing: false });
  }, [persist]);

  const savePreferences = useCallback(
    (prefs: { analytical: boolean; marketing: boolean }) => {
      persist({ functional: true, analytical: prefs.analytical, marketing: prefs.marketing });
    },
    [persist]
  );

  return (
    <CookieContext.Provider
      value={{
        hasConsent,
        preferences,
        cookiewallEnabled,
        acceptAll,
        acceptNecessary,
        savePreferences,
      }}
    >
      {children}
    </CookieContext.Provider>
  );
};

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
};
